import type { ReactNode } from "react";
import type { Detail } from "../snak/SnakContext";
import { Ref } from "../components/Ref";
import { Tag } from "../components/Tag";
import { Placeholder } from "../components/Placeholder";
import { MINT_LIMITATIONS } from "../data/limitations";
export const studyDesignDetail: Detail = {
  kicker: "MINT · Study design",
  title: "A German multicentre real-world study",
  body: <>
      <Tag kind="evidence" data-id="element-255" data-mp="30jnp0p0xw7kv" />
      <Rows rows={[["Design", <>Real-world, observational, non-randomised, multicentre<Ref n={1} data-id="element-257" data-mp="35ih4gmzse8no" /></>], ["Setting", "18 centers in Germany"], ["Patients", <>109 patients with mycosis fungoides (MF) or Sézary syndrome (SS) treated with mogamulizumab <Placeholder data-id="element-258" data-mp="2vs5pm5ekef20">inclusion criteria, MF/SS split, stage and prior-therapy distribution</Placeholder></>], ["Endpoints reported", "Best overall response rate · median progression-free survival · median time to next treatment"], ["Data collection", <Placeholder data-id="element-259" data-mp="2thu0syi7qdmc">retrospective / prospective documentation, observation period, data cut-off</Placeholder>], ["Response criteria", <Placeholder data-id="element-260" data-mp="2ok21vkj5hr8n">e.g. global response per ISCL/EORTC/USCLC consensus — confirm from final materials</Placeholder>]]} data-id="element-256" data-mp="30kfk7yqmk3am" />
      <Limitations data-id="element-261" data-mp="32ci6gprp9xbo" />
    </>
};
export const pfsDetail: Detail = {
  kicker: "MINT · Endpoint",
  title: "Median progression-free survival: 20.2 months",
  body: <>
      <Tag kind="evidence" data-id="element-262" data-mp="3c70cw2oi46z6" />
      <Section title="Definition" data-id="element-263" data-mp="3caeh7ggq6q99">
        Time from start of mogamulizumab to documented disease progression or death from any cause.{" "}
        <Placeholder data-id="element-264" data-mp="3l9je6icn2bjb">confirm exact MINT endpoint definition</Placeholder>
      </Section>
      <Section title="Study context" data-id="element-265" data-mp="3h74wi1kphhtm">
        Reported in 109 patients across 18 German centers in the final MINT results.<Ref n={1} data-id="element-266" data-mp="1qwa0eed283v9" /> Median value; 95% CI{" "}
        <Placeholder data-id="element-267" data-mp="1qx1wdqakk3m2">insert</Placeholder>; median follow-up <Placeholder data-id="element-268" data-mp="1muos76bh0dre">insert</Placeholder>.
      </Section>
      <Section title="How to read it" data-id="element-269" data-mp="3lyery6fj11lc">
        A median from a single-arm, observational cohort describes what was observed in routine care. It is not a
        comparison with any other treatment or with clinical-trial results.
      </Section>
      <Limitations data-id="element-270" data-mp="3vvjei7daaz2o" />
    </>
};
export const ttntDetail: Detail = {
  kicker: "MINT · Endpoint",
  title: "Median time to next treatment: 14.9 months",
  body: <>
      <Tag kind="evidence" data-id="element-271" data-mp="8seorxa8ufoo" />
      <Section title="Definition" data-id="element-272" data-mp="8r3ldykpn9y3">
        Time from start of mogamulizumab to initiation of the next systemic therapy.{" "}
        <Placeholder data-id="element-273" data-mp="dp51qbqr4v5v">confirm exact MINT endpoint definition, including handling of death</Placeholder>
      </Section>
      <Section title="Study context" data-id="element-274" data-mp="ik0ytqaui8pu">
        Reported in 109 patients across 18 German centers in the final MINT results.<Ref n={1} data-id="element-275" data-mp="sg48ribxvbnc" /> 95% CI{" "}
        <Placeholder data-id="element-276" data-mp="sgvttz7wtqzd">insert</Placeholder>.
      </Section>
      <Section title="How to read it" data-id="element-277" data-mp="whfrr50b4ymk">
        Time to next treatment reflects real-world treatment decisions. It is influenced by disease course, tolerability,
        patient preference and physician judgement, and is not a measure of efficacy alone.
      </Section>
      <Limitations data-id="element-278" data-mp="2cwlkvuiwsfgi" />
    </>
};
export const mintResultsDetail: Detail = {
  kicker: "MINT · Final results",
  title: "What the MINT study reported",
  body: <>
      <Tag kind="evidence" data-id="element-279" data-mp="230qu2c4j7e2n" />
      <div className="mt-4 divide-y divide-rule border-y border-rule" data-id="element-280" data-mp="231jxp4871ngk">
        {[["62.4%", "Best overall response rate"], ["20.2 mo", "Median progression-free survival"], ["14.9 mo", "Median time to next treatment"], ["109 / 18", "Patients / German centers"]].map(([v, l]) => <div key={l} className="flex items-baseline justify-between gap-4 py-3" data-id="element-281" data-mp="2lyusocmsluq7">
            <span className="text-[13px] text-charcoal" data-id="element-282" data-mp="2lwqp5vst2xqg">{l}</span>
            <span className="tabular text-[18px] font-light text-ink" data-id="element-283" data-mp="3jfjqlv8mgcub">{v}</span>
          </div>)}
      </div>
      <p className="mt-3 text-[12px] text-muted" data-id="element-284" data-mp="3el5svm6hltmh">
        Source: Kyowa Kirin, final MINT results announcement, October 2026.<Ref n={1} data-id="element-285" data-mp="336uf14v5xlmp" /> Provisional figures, subject to
        verification against the full final study materials.
      </p>
      <Section title="Further detail" data-id="element-286" data-mp="2p7kxa0rb6jgm">
        <Placeholder data-id="element-287" data-mp="2p8mcmhfcxs45">full publication, subgroup analyses (only if reported), safety findings as reported in MINT</Placeholder>
      </Section>
      <Limitations data-id="element-288" data-mp="3hvqieom9yagz" />
    </>
};
export const clinicalEvidenceDetail: Detail = {
  kicker: "Clinical evidence",
  title: "Three sources, three kinds of evidence",
  body: <>
      <p data-id="element-289" data-mp="3mwmo97q6s6p1">Each source answers a different question and should be read on its own terms.</p>
      <div className="mt-5 space-y-6" data-id="element-290" data-mp="383t61ejj5cgo">
        <div data-id="element-291" data-mp="3d4guptkadh7q">
          <Tag kind="trial" data-id="element-292" data-mp="3d3yr2bvao0ps" />
          <p className="mt-2" data-id="element-293" data-mp="3d35u22m50pku">
            Randomised, open-label, international phase 3 trial of mogamulizumab versus vorinostat in 372 adults with
            previously treated MF or SS. Primary endpoint: investigator-assessed progression-free survival. Response was
            also reported by disease compartment (skin, blood, lymph nodes, viscera).<Ref n={2} data-id="element-294" data-mp="1p7heilta0g5c" />
          </p>
        </div>
        <div data-id="element-295" data-mp="1p6pu8754q4ud">
          <Tag kind="evidence" data-id="element-296" data-mp="1p550ukujwry3" />
          <p className="mt-2" data-id="element-297" data-mp="22ye5673n6n9r">
            Real-world, observational, non-randomised data from 109 patients at 18 German centers, describing outcomes in
            routine care.<Ref n={1} data-id="element-298" data-mp="1yz5cz6o92xgb" /> Not designed for comparison with MAVORIC or with other therapies.
          </p>
        </div>
        <div data-id="element-299" data-mp="168dc3vw8pkmn">
          <Tag kind="guidance" data-id="element-300" data-mp="168wtnbhza705" />
          <p className="mt-2" data-id="element-301" data-mp="169oo2izu9you">
            Expert recommendations on the use of mogamulizumab in MF/SS in Germany.<Ref n={3} data-id="element-302" data-mp="1k8xx8ypmnsjb" />{" "}
            <Placeholder data-id="element-303" data-mp="1k5s3v79yu4hs">insert verified key recommendations with page references</Placeholder>
          </p>
        </div>
      </div>
    </>
};
function Section({
  title,
  children
}: {
  title: string;
  children: ReactNode;
}) {
  return <section className="mt-5" data-id="element-304" data-mp="2qoqftpq9uy41">
      <h3 className="text-[12px] font-semibold text-ink" data-id="element-305" data-mp="2qnozp6ueflfg">{title}</h3>
      <div className="mt-1.5" data-id="element-306" data-mp="1obls0mrmqsvp">{children}</div>
    </section>;
}
function Rows({
  rows
}: {
  rows: [string, ReactNode][];
}) {
  return <dl className="mt-4 divide-y divide-rule border-y border-rule" data-id="element-307" data-mp="1t82elcj6kz6o">
      {rows.map(([k, v]) => <div key={k} className="grid grid-cols-[7.5rem_1fr] gap-3 py-2.5 text-[13px]" data-id="element-308" data-mp="1xca59f978p7j">
          <dt className="font-medium text-ink" data-id="element-309" data-mp="22a1we720s7ao">{k}</dt>
          <dd data-id="element-310" data-mp="15kkv04a2l7es">{v}</dd>
        </div>)}
    </dl>;
}
function Limitations() {
  return <section className="mt-6 rounded-lg bg-wash p-4" data-id="element-311" data-mp="19lmvspjmxriv">
      <h3 className="text-[12px] font-semibold text-ink" data-id="element-312" data-mp="19mfx3aztrotm">Limitations</h3>
      <ul className="mt-2 space-y-1.5 text-[13px]" data-id="element-313" data-mp="1ejy5n09xk213">
        {MINT_LIMITATIONS.map(l => <li key={l} className="flex gap-2" data-id="element-314" data-mp="1jbzh4jz9xoq2">
            <span className="mt-[9px] h-px w-3 shrink-0 bg-charcoal" data-id="element-315" data-mp="1jdsndy5z66xj" />
            <span data-id="element-316" data-mp="lvk3q9fq0lk9">{l}</span>
          </li>)}
      </ul>
    </section>;
}